import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../services/KonanAPI"
import type { ScriptGroupList } from "../../types/generated/api/ScriptGroupList"
import type { MenuOption } from "./Interfaces"
import { ScriptTab } from "./Interfaces"
import { ScriptCard } from "./ScriptCard"
import { ScriptDialog } from "./ScriptDialog"

/**
 * @param {Script} ScriptGroupList
 * @return {ReactElement}
 */
export function Container({
  script,
  canShowDetails,
}: Readonly<{ script: ScriptGroupList; canShowDetails: boolean }>): React.ReactElement {
  const { name, uuid, template } = script

  const queryClient = useQueryClient()
  const { id: projectId } = useParams()

  const { t } = useTranslation("scripts")

  const [isDeleteScriptDialogOpen, setIsDeleteScriptDialogOpen] = useState<boolean>(false)
  const [isScriptDialogOpen, setIsScriptDialogOpen] = useState<{
    isOpen: boolean
    isEditMode: boolean
    tab: ScriptTab | undefined
  }>({
    isOpen: false,
    isEditMode: false,
    tab: undefined,
  })

  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)

  const DeleteScriptMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      script_uuid: string
    }
  >(KonanAPI.DeleteScript, {
    onSuccess: async () => {
      NotificationUtils.toast(t("scriptCard.notifications.deleteSuccess"), { severity: "success" })

      await queryClient.invalidateQueries(["scripts", projectId])
    },
    onError: () => {
      NotificationUtils.toast(t("scriptCard.notifications.deleteError"), { severity: "error" })
    },
  })

  return (
    <Fragment>
      {(isDuplicate || isScriptDialogOpen.isOpen) && (
        <ScriptDialog
          isOpen
          onClose={
            isDuplicate
              ? () => setIsDuplicate(false)
              : () => setIsScriptDialogOpen({ isOpen: false, isEditMode: false, tab: undefined })
          }
          scriptId={uuid}
          metaData={script}
          isNewScriptTriggered={isDuplicate}
          isDuplicate={isDuplicate}
          renderView="scripts"
          openInEditMode={isDuplicate ? true : isScriptDialogOpen.isEditMode}
          tab={isDuplicate ? undefined : isScriptDialogOpen.tab}
        />
      )}

      {isDeleteScriptDialogOpen && (
        <BaseSimpleDialog
          isLoading={DeleteScriptMutation.isLoading}
          onAccept={async () =>
            await DeleteScriptMutation.mutateAsync({
              project_uuid: projectId as string,
              script_uuid: uuid,
            })
          }
          mode="script-deletion"
          name={name}
          open
          onClose={() => setIsDeleteScriptDialogOpen(false)}
        />
      )}

      <ScriptCard
        MenuOptions={
          canShowDetails
            ? ([
                {
                  title: t("scriptCard.menuOptions.edit"),
                  variant: "base",
                  onClick: () => {
                    setIsScriptDialogOpen({ isOpen: true, isEditMode: true, tab: undefined })
                  },
                },
                // add duplicate button if custom script
                !template && {
                  title: t("scriptCard.menuOptions.duplicate"),
                  variant: "base",
                  onClick: () => {
                    setIsDuplicate(true)
                  },
                },
                {
                  title: t("scriptCard.menuOptions.delete"),
                  variant: "negative",
                  onClick: () => setIsDeleteScriptDialogOpen(true),
                },
              ].filter(Boolean) as MenuOption[])
            : ([
                {
                  title: t("scriptCard.menuOptions.test"),
                  onClick: () => {
                    setIsScriptDialogOpen({ isOpen: true, isEditMode: false, tab: ScriptTab.TestFeatures })
                  },
                },
              ] as MenuOption[])
        }
        onNameClick={() => {
          setIsScriptDialogOpen({ isOpen: true, isEditMode: false, tab: undefined })
        }}
        onLogsDialogClick={() => {
          setIsScriptDialogOpen({ isOpen: true, isEditMode: false, tab: ScriptTab.Logs })
        }}
        script={script}
      />
    </Fragment>
  )
}
